import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
  styled,
} from '@mui/material';
import { useId } from 'react';
import { theme } from '~/shared/styles/theme';

interface SelectOption {
  value: string;
  label: string;
}

interface Props<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label: string;
  options: readonly SelectOption[];
}

const FormMultiSelect = <T extends FieldValues>({
  name,
  control,
  label,
  options,
}: Props<T>) => {
  const labelId = useId();

  return (
    <Stack width="100%" sx={{ pb: 2 }}>
      <Label id={labelId} variant="body2">
        {label}
      </Label>
      <FormControl fullWidth>
        <Controller
          name={name}
          control={control}
          render={({
            field: { value, onChange, onBlur, name: fieldName, ref },
          }) => {
            const selected: string[] = Array.isArray(value) ? value : [];
            const handleChange = (optionValue: string, checked: boolean) => {
              let next: string[];
              if (checked) {
                next = selected.includes(optionValue)
                  ? selected
                  : [...selected, optionValue];
              } else {
                next = selected.filter((value) => value !== optionValue);
              }
              onChange(next);
            };
            return (
              <FormGroup aria-labelledby={labelId}>
                {options.map(
                  ({ value: optionValue, label: optionLabel }, index) => (
                    <OptionRow
                      key={optionValue}
                      control={
                        <Checkbox
                          size="small"
                          checked={selected.includes(optionValue)}
                          onChange={(_e, checked) =>
                            handleChange(optionValue, checked)
                          }
                          onBlur={onBlur}
                          name={fieldName}
                          ref={index === 0 ? ref : undefined}
                          icon={<CheckboxBox />}
                          checkedIcon={
                            <CheckedCheckboxBox>
                              <svg viewBox="0 0 24 24">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                              </svg>
                            </CheckedCheckboxBox>
                          }
                        />
                      }
                      label={
                        <OptionLabel
                          sx={
                            selected.includes(optionValue)
                              ? { fontWeight: 700 }
                              : undefined
                          }
                        >
                          {optionLabel}
                        </OptionLabel>
                      }
                    />
                  ),
                )}
              </FormGroup>
            );
          }}
        />
      </FormControl>
    </Stack>
  );
};

export default FormMultiSelect;

const Label = styled(Typography)({
  fontSize: 12,
  color: theme.palette.custom.cohortColumnHeader,
});

const OptionLabel = styled(Typography)({
  fontSize: 14,
  color: theme.palette.custom.filterAccent,
});

const OptionRow = styled(FormControlLabel)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));

const CheckboxBox = styled(Box)(({ theme }) => ({
  width: 16,
  height: 16,
  borderRadius: 5,
  border: `1px solid ${theme.palette.custom.filterAccent}`,
  boxSizing: 'border-box',
}));

const CheckedCheckboxBox = styled(CheckboxBox)(({ theme }) => ({
  backgroundColor: theme.palette.custom.filterAccent,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& svg': {
    width: 16,
    height: 16,
    fill: theme.palette.common.white,
  },
}));
